import { LocationIcon as LocationSvg } from '@/assets/icons'
import { AttachmentsViewerModal } from '@components/Attachments'
import ConfirmationModal from '@components/ConfirmationModal'
import PageLoader from '@components/PageLoader'
import { useAuth } from '@context/AuthContext'
import { useBreadcrumbs } from '@context/BreadcrumbContext'
import { useTrip, useTripMembers } from '@hooks'
import { useAccommodations } from '@hooks/useAccommodations'
import { useDeleteAccommodation } from '@hooks/useDeleteAccommodation'
import { UpdateAccommodationPayload, useUpdateAccommodation } from '@hooks/useUpdateAccommodation'
import { normalizeExternalUrl } from '@lib/utils'
import { formatUtcInstant } from '@lib/datetime'
import { GetAccommodationResponse, GetUserType } from '@orbit/api-types'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import EditAccommodationModal from '../EditAccommodationModal'
import {
    ActionButtons,
    AttachmentButton,
    DateBlock,
    DateBlockLabel,
    DateBlockTime,
    DateBlockValue,
    DatesSection,
    DetailBody,
    DetailCard,
    DetailHeader,
    DetailPageContainer,
    DetailTitle,
    ErrorContainer,
    GuestAvatar,
    GuestItem,
    GuestList,
    GuestName,
    IconButton,
    LinkItem,
    LinksList,
    LoadingContainer,
    LocationIcon,
    LocationPrimary,
    LocationSecondary,
    LocationSection,
    LocationText,
    MapButton,
    MetaSection,
    NotesContent,
    NotFoundMessage,
    ScrollContainer,
    SectionLabel,
    YouPill,
} from './AccommodationDetailPage.styles'

const getGoogleMapsUrl = (accommodation: GetAccommodationResponse): string | null => {
    if (!accommodation.location) return null
    if (accommodation.location.googlePlaceId) {
        return `https://www.google.com/maps/place/?q=place_id:${accommodation.location.googlePlaceId}`
    }
    const query = accommodation.location.fullText || accommodation.location.primaryText
    if (query) {
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
    }
    return null
}

const AccommodationDetailPage: React.FC = () => {
    const { tripId, accommodationId } = useParams<{ tripId: string; accommodationId: string }>()
    const navigate = useNavigate()
    const { user } = useAuth()
    const { data: trip, isLoading: tripLoading } = useTrip(tripId)
    const { data: accommodationsData, isLoading: accommodationsLoading, error: accommodationsError } = useAccommodations(tripId)
    const { data: membersData } = useTripMembers(tripId)

    const updateAccommodation = useUpdateAccommodation()
    const deleteAccommodation = useDeleteAccommodation()

    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [isAttachmentsViewerOpen, setIsAttachmentsViewerOpen] = useState(false)

    const members = membersData?.members ?? []
    const accommodation = accommodationsData?.accommodations?.find(
        a => a.id === Number(accommodationId)
    )

    useBreadcrumbs(
        trip
            ? [
                { label: 'My Trips', path: '/trips' },
                { label: trip.name, path: `/trips/${tripId}` },
                { label: 'Accommodations', path: `/trips/${tripId}/accommodations` },
                { label: accommodation?.name ?? 'Details' },
            ]
            : []
    )

    const getMemberById = (userId: number): GetUserType | undefined => {
        return members.find(m => m.id === userId)
    }

    const getInitials = (member: GetUserType): string => {
        const first = member.firstName?.[0] ?? ''
        const last = member.lastName?.[0] ?? ''
        return (first + last).toUpperCase() || '?'
    }

    const getMemberName = (member: GetUserType): string => {
        return [member.firstName, member.lastName].filter(Boolean).join(' ')
    }

    const handleEditAccommodation = (data: UpdateAccommodationPayload) => {
        if (!tripId || !accommodation) return
        updateAccommodation.mutate(
            { tripId, accommodationId: accommodation.id, accommodationData: data },
            {
                onSuccess: () => {
                    toast.success('Accommodation updated!')
                    setIsEditModalOpen(false)
                },
                onError: () => {
                    toast.error('Failed to update accommodation. Please try again.')
                },
            }
        )
    }

    const handleDeleteAccommodation = () => {
        if (!tripId || !accommodation) return
        deleteAccommodation.mutate(
            { tripId, accommodationId: accommodation.id },
            {
                onSuccess: () => {
                    toast.success('Accommodation deleted')
                    navigate(`/trips/${tripId}/accommodations`)
                },
                onError: () => {
                    toast.error('Failed to delete accommodation. Please try again.')
                },
            }
        )
    }

    if (tripLoading || accommodationsLoading) {
        return (
            <LoadingContainer>
                <PageLoader label="Loading accommodation..." />
            </LoadingContainer>
        )
    }

    if (accommodationsError) {
        return (
            <ErrorContainer>
                Error loading accommodation: {accommodationsError.message}
            </ErrorContainer>
        )
    }

    if (!trip || !accommodation) {
        return (
            <LoadingContainer>
                <NotFoundMessage>
                    Accommodation not found
                </NotFoundMessage>
            </LoadingContainer>
        )
    }

    const mapsUrl = getGoogleMapsUrl(accommodation)
    const guests = accommodation.guests ?? []
    const links = accommodation.links ?? []
    const fileIds = accommodation.fileIds ?? []
    const createdBy = getMemberById(accommodation.createdByUserId)

    return (
        <DetailPageContainer>
            <ScrollContainer>
                <DetailCard>
                    <DetailHeader>
                        <DetailTitle>{accommodation.name}</DetailTitle>
                        <ActionButtons>
                            <IconButton onClick={() => setIsEditModalOpen(true)} title="Edit">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                            </IconButton>
                            <IconButton $variant="danger" onClick={() => setIsDeleteModalOpen(true)} title="Delete">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6" />
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                </svg>
                            </IconButton>
                        </ActionButtons>
                    </DetailHeader>

                    <DetailBody>
                        {/* Location */}
                        {accommodation.location && (
                            <LocationSection>
                                <LocationIcon>
                                    <LocationSvg width={20} height={20} fill="currentColor" />
                                </LocationIcon>
                                <LocationText>
                                    <LocationPrimary>
                                        {accommodation.location.primaryText || accommodation.location.fullText}
                                    </LocationPrimary>
                                    {accommodation.location.secondaryText && (
                                        <LocationSecondary>{accommodation.location.secondaryText}</LocationSecondary>
                                    )}
                                </LocationText>
                                {mapsUrl && (
                                    <MapButton href={mapsUrl} target="_blank" rel="noopener noreferrer">
                                        View Map
                                    </MapButton>
                                )}
                            </LocationSection>
                        )}

                        {/* Dates */}
                        <DatesSection>
                            <DateBlock>
                                <DateBlockLabel>Check-in</DateBlockLabel>
                                <DateBlockValue>{formatUtcInstant(accommodation.checkInDate, 'dateWeekday')}</DateBlockValue>
                                <DateBlockTime>{formatUtcInstant(accommodation.checkInDate, 'time')}</DateBlockTime>
                            </DateBlock>
                            <DateBlock $align="right">
                                <DateBlockLabel>Check-out</DateBlockLabel>
                                <DateBlockValue>{formatUtcInstant(accommodation.checkOutDate, 'dateWeekday')}</DateBlockValue>
                                <DateBlockTime>{formatUtcInstant(accommodation.checkOutDate, 'time')}</DateBlockTime>
                            </DateBlock>
                        </DatesSection>

                        {/* Guests */}
                        <div>
                            <SectionLabel>Guests ({guests.length})</SectionLabel>
                            <GuestList>
                                {guests.map(guest => {
                                    const member = getMemberById(guest.userId)
                                    if (!member) return null
                                    const isYou = user?.id === guest.userId
                                    return (
                                        <GuestItem key={guest.userId}>
                                            <GuestAvatar>
                                                {member.profilePicture ? (
                                                    <img src={member.profilePicture} alt={getInitials(member)} />
                                                ) : (
                                                    getInitials(member)
                                                )}
                                            </GuestAvatar>
                                            <GuestName>{getMemberName(member)}</GuestName>
                                            {isYou && <YouPill>You</YouPill>}
                                        </GuestItem>
                                    )
                                })}
                            </GuestList>
                        </div>

                        {/* Links */}
                        {links.length > 0 && (
                            <div>
                                <SectionLabel>Links ({links.length})</SectionLabel>
                                <LinksList>
                                    {links.map((link, index) => (
                                        <LinkItem
                                            key={index}
                                            href={normalizeExternalUrl(link)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {link}
                                        </LinkItem>
                                    ))}
                                </LinksList>
                            </div>
                        )}

                        {/* Notes */}
                        {accommodation.notes && (
                            <div>
                                <SectionLabel>Notes</SectionLabel>
                                <NotesContent>{accommodation.notes}</NotesContent>
                            </div>
                        )}

                        {/* Attachments */}
                        {fileIds.length > 0 && (
                            <div>
                                <SectionLabel>Attachments ({fileIds.length})</SectionLabel>
                                <AttachmentButton type="button" onClick={() => setIsAttachmentsViewerOpen(true)}>
                                    View {fileIds.length} {fileIds.length === 1 ? 'attachment' : 'attachments'}
                                </AttachmentButton>
                            </div>
                        )}

                        {/* Meta */}
                        <MetaSection>
                            {createdBy && (
                                <span>Added by {getMemberName(createdBy)}</span>
                            )}
                        </MetaSection>
                    </DetailBody>
                </DetailCard>
            </ScrollContainer>

            {accommodation && (
                <EditAccommodationModal
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    onSubmit={handleEditAccommodation}
                    onDelete={() => {
                        setIsEditModalOpen(false)
                        setIsDeleteModalOpen(true)
                    }}
                    accommodation={accommodation}
                    members={members}
                    isSubmitting={updateAccommodation.isPending}
                />
            )}

            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDeleteAccommodation}
                title="Delete Accommodation"
                message={`Are you sure you want to delete "${accommodation?.name}"? This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                variant="danger"
            />

            {tripId && (
                <AttachmentsViewerModal
                    isOpen={isAttachmentsViewerOpen}
                    onClose={() => setIsAttachmentsViewerOpen(false)}
                    tripId={tripId}
                    fileIds={fileIds}
                    title="Attachments"
                    onEditClick={() => setIsEditModalOpen(true)}
                />
            )}
        </DetailPageContainer>
    )
}

export default AccommodationDetailPage
